import { createUnverifySizedCollectionItemInstruction, createUnverifyCollectionInstruction, createUnverifyInstruction, VerificationArgs } from '@metaplex-foundation/mpl-token-metadata';
import { SYSVAR_INSTRUCTIONS_PUBKEY } from '@solana/web3.js';
import { useOperation } from '../../../types/Operation.mjs';
import { TransactionBuilder } from '../../../utils/TransactionBuilder.mjs';

// -----------------
// Operation
// -----------------

const Key = 'UnverifyNftCollectionOperation';

/**
 * Unverifies the collection of an NFT or SFT.
 *
 * ```ts
 * await metaplex
 *   .nfts()
 *   .unverifyCollection({ mintAddress, collectionMintAddress };
 * ```
 *
 * @group Operations
 * @category Constructors
 */
const unverifyNftCollectionOperation = useOperation(Key);

/**
 * @group Operations
 * @category Types
 */

/**
 * @group Operations
 * @category Handlers
 */
const unverifyNftCollectionOperationHandler = {
  handle: async (operation, metaplex, scope) => {
    return unverifyNftCollectionBuilder(metaplex, operation.input, scope).sendAndConfirm(metaplex, scope.confirmOptions);
  }
};

// -----------------
// Builder
// -----------------

/**
 * @group Transaction Builders
 * @category Inputs
 */

/**
 * Unverifies the collection of an NFT or SFT.
 *
 * ```ts
 * const transactionBuilder = metaplex
 *   .nfts()
 *   .builders()
 *   .unverifyCollection({ mintAddress, collectionMintAddress });
 * ```
 *
 * @group Transaction Builders
 * @category Constructors
 */
const unverifyNftCollectionBuilder = (metaplex, params, options = {}) => {
  const {
    programs,
    payer = metaplex.rpc().getDefaultFeePayer()
  } = options;
  const {
    mintAddress,
    collectionMintAddress,
    isSizedCollection = true,
    isDelegated = false,
    collectionAuthority = metaplex.identity(),
    collectionUpdateAuthority = metaplex.identity().publicKey
  } = params;

  // Programs.
  const systemProgram = metaplex.programs().getSystem(programs);
  const tokenMetadataProgram = metaplex.programs().getTokenMetadata(programs);

  // Accounts.
  const metadata = metaplex.nfts().pdas().metadata({
    mint: mintAddress,
    programs
  });
  const collectionMetadata = metaplex.nfts().pdas().metadata({
    mint: collectionMintAddress,
    programs
  });
  const collectionEdition = metaplex.nfts().pdas().masterEdition({
    mint: collectionMintAddress,
    programs
  });
  if (isDelegated === 'legacyDelegate' || isDelegated === true) {
    const accounts = {
      metadata,
      collectionAuthority: collectionAuthority.publicKey,
      payer: payer.publicKey,
      collectionMint: collectionMintAddress,
      collection: collectionMetadata,
      collectionMasterEditionAccount: collectionEdition,
      collectionAuthorityRecord: metaplex.nfts().pdas().collectionAuthorityRecord({
        mint: collectionMintAddress,
        collectionAuthority: collectionAuthority.publicKey,
        programs
      })
    };
    const instruction = isSizedCollection ? createUnverifySizedCollectionItemInstruction(accounts, tokenMetadataProgram.address) : createUnverifyCollectionInstruction(accounts, tokenMetadataProgram.address);
    return TransactionBuilder.make().setFeePayer(payer).add({
      instruction,
      signers: [payer, collectionAuthority],
      key: params.instructionKey ?? 'unverifyCollection'
    });
  }
  const delegateRecord = isDelegated === 'metadataDelegate' ? metaplex.nfts().pdas().metadataDelegateRecord({
    mint: collectionMintAddress,
    type: 'CollectionV1',
    updateAuthority: collectionUpdateAuthority,
    delegate: collectionAuthority.publicKey,
    programs
  }) : undefined;
  return TransactionBuilder.make().setFeePayer(payer).add({
    instruction: createUnverifyInstruction({
      authority: collectionAuthority.publicKey,
      delegateRecord,
      metadata,
      collectionMint: collectionMintAddress,
      collectionMetadata,
      systemProgram: systemProgram.address,
      sysvarInstructions: SYSVAR_INSTRUCTIONS_PUBKEY
    }, {
      verificationArgs: VerificationArgs.CollectionV1
    }, tokenMetadataProgram.address),
    signers: [collectionAuthority],
    key: params.instructionKey ?? 'unverifyCollection'
  });
};

export { unverifyNftCollectionBuilder, unverifyNftCollectionOperation, unverifyNftCollectionOperationHandler };
//# sourceMappingURL=unverifyNftCollection.mjs.map
